/**
 * Left dock + drawers: Production (queue, build buttons, deploy city), Research (four branch
 * columns of tech cards), Intelligence (intercepted enemy orders, known formations) and the
 * Meteorological Service (MetDrawer.tsx).
 */
import {
  type CityId,
  MAX_PARALLEL_PRODUCTION,
  TECHS,
  type TechBranch,
  type TechId,
  UNIT_STATS,
  UNIT_TYPES,
  type UnitType,
} from '@friction/engine';
import { useEffect, useMemo } from 'react';
import { bus } from '../../game/bus';
import {
  factionOf,
  formatAge,
  formatHHour,
  orderLabel,
  placeName,
  postureLabel,
  unitTypeLabel,
} from '../../game/format';
import { gameStore, useGame } from '../../game/store';
import { useT } from '../../i18n';
import { cityName, nationLabel, unitName } from '../../i18n/names';
import { Bar, cx, Select } from '../common/controls';
import { Icon, type IconName } from '../common/Icon';
import { UnitCounter } from '../common/UnitCounter';
import { DrawerHead } from './DrawerHead';
import d from './drawers.module.css';
import hud from './hud.module.css';
import { type HudPanel, hudStore, useHud } from './hudStore';
import { MetDrawer } from './MetDrawer';

const DOCK: { panel: HudPanel; icon: IconName; key: string }[] = [
  { panel: 'production', icon: 'factory', key: 'P' },
  { panel: 'research', icon: 'research', key: 'T' },
  { panel: 'intel', icon: 'intel', key: 'I' },
  { panel: 'weather', icon: 'cloud', key: 'W' },
];

export function Dock() {
  const t = useT();
  const panel = useHud((h) => h.panel);
  const seen = useHud((h) => h.intelSeenAt);
  const unread = useGame((g) => g.view?.intercepts.filter((i) => i.tick > seen).length ?? 0);
  return (
    <nav className={cx(hud.plate, hud.dock)} aria-label={t('dock.aria')}>
      {DOCK.map((b) => (
        <button
          key={b.panel}
          type="button"
          className={cx(hud.dockBtn, panel === b.panel && hud.dockOn)}
          data-tut={b.panel}
          title={`${t(`dock.${b.panel}.title`)} (${b.key})`}
          onClick={() => {
            bus.emit('sound', { name: 'paper' });
            hudStore.getState().togglePanel(b.panel);
          }}
        >
          <Icon name={b.icon} size={20} />
          <span className={hud.dockLabel}>{t(`dock.${b.panel}`)}</span>
          <span className={hud.dockKey}>{b.key}</span>
          {b.panel === 'intel' && unread > 0 && panel !== 'intel' && (
            <span className={hud.dockBadge}>{unread}</span>
          )}
        </button>
      ))}
    </nav>
  );
}

// ---- production ---------------------------------------------------------------------------------

function Production() {
  const t = useT();
  const view = useGame((g) => g.view);
  const map = useGame((g) => g.map);
  const chosen = useHud((h) => h.productionCity);
  const cities = useMemo(() => {
    if (!view || !map) return [];
    return map.cities
      .filter((c) => view.owner[c.hex] === view.faction && view.supply[c.hex] > 0)
      .sort((a, b) => b.size - a.size || a.name.localeCompare(b.name, t.locale));
  }, [view, map, t.locale]);
  if (!view || !map) return null;
  const me = view.me;
  const city: CityId | null = cities.some((c) => c.id === chosen)
    ? chosen
    : (cities.find((c) => c.id === me.capitalCity)?.id ?? cities[0]?.id ?? null);
  const active = Math.min(MAX_PARALLEL_PRODUCTION, me.production.length);
  const perItem = active ? me.industryRate / active : me.industryRate;

  return (
    <>
      <DrawerHead
        title={t('prod.title')}
        sub={t('prod.sub', { rate: t.num(me.industryRate, 1), men: Math.round(me.manpower) })}
      />
      <div className={hud.drawerBody}>
        <div className={d.section}>
          <div className={d.sectionTitle}>
            <span>{t('prod.book')}</span>
            <span>{t('prod.lines', { a: active, b: MAX_PARALLEL_PRODUCTION })}</span>
          </div>
          {me.production.length === 0 && <div className={d.muted}>{t('prod.idle')}</div>}
          {me.production.map((item, i) => {
            const cost = UNIT_STATS[item.unitType].cost.industry;
            const running = i < MAX_PARALLEL_PRODUCTION;
            const eta = running && perItem > 0 ? Math.ceil((cost - item.progress) / perItem) : null;
            return (
              <div key={item.id} className={cx(d.item, !running && d.itemQueued)}>
                <UnitCounter type={item.unitType} color={me.color} size={40} echelon={false} />
                <div>
                  <div className={d.itemName}>
                    <span>{unitTypeLabel(item.unitType, t.locale)}</span>
                    <span className={d.itemMeta}>
                      {running ? (eta !== null ? formatAge(eta, t.locale) : '—') : t('prod.queued')}
                    </span>
                  </div>
                  <Bar
                    value={item.progress / cost}
                    tone="brass"
                    display={`${Math.floor(item.progress)}/${cost}`}
                  />
                  <div className={d.itemMeta} style={{ marginTop: 3 }}>
                    {t('prod.at', { city: map.cities[item.cityId]?.name ?? '—' })}
                  </div>
                </div>
                <button
                  type="button"
                  className={d.x}
                  title={t('prod.cancel')}
                  onClick={() =>
                    gameStore.getState().sendCommand({ type: 'cancelProduction', itemId: item.id })
                  }
                >
                  <Icon name="close" size={14} />
                </button>
              </div>
            );
          })}
        </div>

        <div className={d.section}>
          <div className={d.sectionTitle}>
            <span>{t('prod.deployAt')}</span>
          </div>
          {cities.length ? (
            <Select
              value={city ?? -1}
              options={cities.map((c) => ({
                value: c.id,
                label: t(c.id === me.capitalCity ? 'prod.capitalCity' : 'prod.city', {
                  name: cityName(c.name, t.locale),
                  kind: t(`city.size.${c.size as 1 | 2 | 3}`),
                }),
              }))}
              onChange={(v) => hudStore.getState().setProductionCity(v)}
            />
          ) : (
            <div className={d.muted}>{t('prod.noCity')}</div>
          )}
        </div>

        <div className={d.section}>
          <div className={d.sectionTitle}>
            <span>{t('prod.new')}</span>
          </div>
          <div className={d.buildGrid}>
            {UNIT_TYPES.filter((u) => UNIT_STATS[u].buildable).map((type: UnitType) => {
              const st = UNIT_STATS[type];
              const locked = st.requires !== null && !me.research.unlocked.includes(st.requires);
              const shortMen = me.manpower < st.cost.manpower;
              return (
                <button
                  key={type}
                  type="button"
                  className={d.build}
                  disabled={locked || shortMen || city === null}
                  title={
                    locked
                      ? t('prod.requires', { tech: t(`tech.${st.requires!}.name`) })
                      : t('prod.cost', {
                          type: unitTypeLabel(type, t.locale),
                          ind: st.cost.industry,
                          men: st.cost.manpower,
                        })
                  }
                  onClick={() => {
                    if (city === null) return;
                    gameStore.getState().sendCommand({ type: 'produce', unitType: type, cityId: city });
                    bus.emit('sound', { name: 'stamp', volume: 0.5 });
                  }}
                >
                  <UnitCounter type={type} color={me.color} size={38} echelon={false} />
                  <span>
                    <span className={d.buildName}>{unitTypeLabel(type, t.locale)}</span>
                    {locked ? (
                      <span className={d.lock}>
                        <Icon name="lock" size={11} /> {t(`tech.${st.requires!}.name`)}
                      </span>
                    ) : (
                      <span className={d.cost}>
                        <span>
                          <Icon name="factory" size={11} />
                          {st.cost.industry}
                        </span>
                        <span className={cx(shortMen && d.short)}>
                          <Icon name="manpower" size={11} />
                          {t('prod.men', { men: st.cost.manpower })}
                        </span>
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

// ---- research -----------------------------------------------------------------------------------

const BRANCHES: { id: TechBranch; icon: IconName }[] = [
  { id: 'signals', icon: 'radio' },
  { id: 'intelligence', icon: 'eye' },
  { id: 'warfare', icon: 'swords' },
  { id: 'logistics', icon: 'map' },
];

/** Techs of a branch in depth-first order: every tech directly follows its prerequisite chain. */
function branchOrder(branch: TechBranch): TechId[] {
  const techs = Object.values(TECHS).filter((t) => t.branch === branch);
  const inBranch = (id: TechId) => TECHS[id].branch === branch;
  const out: TechId[] = [];
  const visit = (id: TechId) => {
    if (out.includes(id)) return;
    out.push(id);
    for (const t of techs)
      if (t.requires.includes(id) && t.requires.every((r) => !inBranch(r) || out.includes(r))) visit(t.id);
  };
  for (const t of techs) if (!t.requires.some(inBranch)) visit(t.id);
  for (const t of techs) if (!out.includes(t.id)) out.push(t.id);
  return out;
}

function Research() {
  const t = useT();
  const techName = (id: TechId) => t(`tech.${id}.name`);
  const me = useGame((g) => g.view?.me);
  const orders = useMemo(
    () => Object.fromEntries(BRANCHES.map((b) => [b.id, branchOrder(b.id)])) as Record<TechBranch, TechId[]>,
    [],
  );
  if (!me) return null;
  const { current, progress, unlocked } = me.research;
  const cur = current ? TECHS[current] : null;
  const eta = cur && me.researchRate > 0 ? Math.ceil((cur.cost - progress) / me.researchRate) : null;
  return (
    <>
      <DrawerHead
        title={t('res.title')}
        sub={t('res.sub', {
          rate: t.num(me.researchRate, 2),
          n: unlocked.length,
          total: Object.keys(TECHS).length,
        })}
      />
      <div className={hud.drawerBody}>
        {cur ? (
          <div className={d.current}>
            <span className={d.currentName}>{techName(cur.id)}</span>
            <span className={d.currentEta}>
              {eta !== null ? t('res.eta', { eta: formatAge(eta, t.locale) }) : '—'}
            </span>
            <div style={{ gridColumn: '1 / -1' }}>
              <Bar value={progress / cur.cost} tone="brass" display={`${Math.floor(progress)}/${cur.cost}`} />
            </div>
          </div>
        ) : (
          <div className={d.current}>
            <span className={d.currentName} style={{ color: 'var(--muted)' }}>
              {t('res.none')}
            </span>
            <span className={d.currentEta}>{t('res.chooseBelow')}</span>
          </div>
        )}
        <div className={d.branches}>
          {BRANCHES.map((b) => (
            <div key={b.id} className={d.branch}>
              <div className={d.branchHead}>
                <Icon name={b.icon} size={14} />
                {t(`branch.${b.id}`)}
              </div>
              {orders[b.id].map((id) => {
                const tech = TECHS[id];
                const done = unlocked.includes(id);
                const now = current === id;
                const missing = tech.requires.filter((r) => !unlocked.includes(r));
                const locked = !done && missing.length > 0;
                const dep = tech.requires.some((r) => TECHS[r].branch === b.id);
                const desc = t(`tech.${id}.desc`);
                const needs = missing.map(techName).join(', ');
                return (
                  <button
                    key={id}
                    type="button"
                    className={cx(
                      d.tech,
                      dep && d.techDep,
                      done && d.techDone,
                      now && d.techNow,
                      locked && d.techLocked,
                    )}
                    disabled={done || locked || now}
                    title={locked ? t('res.requires', { list: needs }) : desc}
                    onClick={() => {
                      gameStore.getState().sendCommand({ type: 'research', tech: id });
                      bus.emit('sound', { name: 'confirm' });
                    }}
                  >
                    <span className={d.techName}>
                      {techName(id)}
                      <span className={d.techCost}>{tech.cost}</span>
                    </span>
                    <span className={d.techDesc}>{desc}</span>
                    {locked && <span className={d.techReq}>{t('res.needs', { list: needs })}</span>}
                    {done && (
                      <span className={d.techState} style={{ color: 'var(--ok-hi)' }}>
                        <Icon name="check" size={12} /> {t('res.adopted')}
                      </span>
                    )}
                    {now && (
                      <>
                        <span className={d.techState} style={{ color: 'var(--brass-hi)' }}>
                          <Icon name="hourglass" size={12} /> {t('res.inDevelopment')}
                        </span>
                        <span className={d.progress}>
                          <i style={{ width: `${(progress / tech.cost) * 100}%` }} />
                        </span>
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ---- intelligence -------------------------------------------------------------------------------

function Intel() {
  const t = useT();
  const view = useGame((g) => g.view);
  const map = useGame((g) => g.map);
  const grid = useGame((g) => g.grid);
  useEffect(() => {
    if (view) hudStore.getState().markIntelSeen(view.tick);
  }, [view]);
  if (!view || !map || !grid) return null;
  const ctx = { map, grid };
  const observed = view.contacts.filter((c) => c.visibleNow).length;
  const decoys = view.contacts.filter((c) => c.exposedDecoy).length;
  const contacts = [...view.contacts].sort((a, b) => b.lastSeen - a.lastSeen);
  const intercepts = [...view.intercepts].sort((a, b) => b.tick - a.tick);
  return (
    <>
      <DrawerHead title={t('intel.title')} sub={t('intel.sub', { n: view.me.stats.ordersIntercepted })} />
      <div className={hud.drawerBody}>
        <div className={d.section}>
          <div className={d.stats}>
            <div className={d.stat}>
              <div className={d.statValue}>{observed}</div>
              <div className={d.statLabel}>{t('intel.observed')}</div>
            </div>
            <div className={d.stat}>
              <div className={d.statValue}>{view.contacts.length - observed}</div>
              <div className={d.statLabel}>{t('intel.ghosts')}</div>
            </div>
            <div className={d.stat}>
              <div className={d.statValue}>{decoys}</div>
              <div className={d.statLabel}>{t('intel.decoys')}</div>
            </div>
          </div>
        </div>

        <div className={d.section}>
          <div className={d.sectionTitle}>
            <span>{t('intel.intercepts')}</span>
            <span>{intercepts.length}</span>
          </div>
          {intercepts.length === 0 && <div className={d.muted}>{t('intel.noIntercepts')}</div>}
          {intercepts.map((ic) => {
            const f = factionOf(view, ic.victim);
            const o = ic.order;
            const verb =
              o.kind === 'posture'
                ? t('intel.posture', { posture: o.posture ? postureLabel(o.posture, t.locale) : '—' })
                : t('intel.order', {
                    order: orderLabel(o.kind, t.locale),
                    place: placeName(ctx, o.target, t.locale),
                  });
            return (
              <button
                key={ic.id}
                type="button"
                className={d.intercept}
                onClick={() => bus.emit('focusHex', { hex: o.target })}
                title={t('intel.showPlan')}
              >
                <span className={d.intIcon}>
                  <Icon name={ic.method === 'captured-courier' ? 'courier' : 'radio'} size={16} />
                </span>
                <span>
                  <span className={d.intHead}>
                    {ic.method === 'captured-courier' ? t('intel.courier') : t('intel.radio')}
                    <span className={d.intAge}>
                      {t('intel.age', {
                        age: formatAge(view.tick - ic.tick, t.locale),
                        left: formatAge(Math.max(0, ic.expiresAt - view.tick), t.locale),
                      })}
                    </span>
                  </span>
                  <div className={d.intUnit} style={{ color: f ? undefined : 'inherit' }}>
                    {unitName(ic.unitName, t.locale)}
                    {f && (
                      <span style={{ color: 'var(--muted)', fontWeight: 400 }}>
                        {' '}
                        · {nationLabel(f, t.locale)}
                      </span>
                    )}
                  </div>
                  <div className={d.intOrder}>
                    {verb}
                    {o.executeAt >= 0 && ` · ${formatHHour(o.executeAt, t.locale)}`}
                    {o.opName && (t.locale === 'de' ? ` · „${o.opName}“` : ` · “${o.opName}”`)}
                  </div>
                </span>
              </button>
            );
          })}
        </div>

        <div className={d.section}>
          <div className={d.sectionTitle}>
            <span>{t('intel.known')}</span>
            <span>{contacts.length}</span>
          </div>
          {contacts.length === 0 && <div className={d.muted}>{t('intel.noContacts')}</div>}
          {contacts.map((c) => {
            const f = factionOf(view, c.faction);
            return (
              <button
                key={c.unitId}
                type="button"
                className={d.contactRow}
                onClick={() => {
                  const g = gameStore.getState();
                  g.select([]);
                  g.inspectContact(c.unitId);
                  bus.emit('focusHex', { hex: c.hex });
                }}
              >
                <UnitCounter
                  type={c.level >= 2 ? c.type : null}
                  color={f?.color ?? 0x777777}
                  size={32}
                  echelon={false}
                  ghost={!c.visibleNow}
                  exposed={c.exposedDecoy}
                />
                <span style={{ minWidth: 0 }}>
                  <div className={d.contactName}>
                    {c.name
                      ? unitName(c.name, t.locale)
                      : c.type && c.level >= 2
                        ? t(`unit.formation.${c.type}`)
                        : t('unit.unidentifiedContact')}
                  </div>
                  <div className={d.contactPlace}>{placeName(ctx, c.hex, t.locale)}</div>
                </span>
                <span className={d.contactAge}>
                  {c.visibleNow ? t('time.now') : formatAge(view.tick - c.lastSeen, t.locale)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

export function Drawer() {
  const panel = useHud((h) => h.panel);
  if (!panel) return null;
  return (
    <section className={cx(hud.plate, hud.drawer, panel === 'research' && hud.drawerWide)} key={panel}>
      {panel === 'production' ? (
        <Production />
      ) : panel === 'research' ? (
        <Research />
      ) : panel === 'weather' ? (
        <MetDrawer />
      ) : (
        <Intel />
      )}
    </section>
  );
}
